import React, { useState, useEffect, useContext, useRef } from 'react';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBuilding, 
    faBell, 
    faTrophy, 
    faUsers, 
    faArrowLeft,
    faPlus,
    faExclamationTriangle,
    faExternalLinkAlt,
    faDownload,
    faEye,
    faCrown,
    faMedal,
    faStar,
    faCheck,
    faClock,
    faCalendarAlt,
    faHistory,
    faChartLine,
    faTimes,
    faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './header';
import Footer from './footer';
import { useCustomAlert } from './CustomAlert';
import { timeAgo } from '../utils/functions';

const DepartmentDetail = () => {
    const { user } = useContext(MyContext);
    const { groupId, departmentId } = useParams();
    const navigate = useNavigate();
    const { showAlert, AlertComponent } = useCustomAlert();
    
    const [department, setDepartment] = useState(null);
    const [group, setGroup] = useState(null);
    const [announcements, setAnnouncements] = useState([]);
    const [goals, setGoals] = useState([]);
    const [topPerformers, setTopPerformers] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState('member');
    const [isDepartmentMember, setIsDepartmentMember] = useState(false);
    const [isDepartmentLeader, setIsDepartmentLeader] = useState(false);
    const [showCreateAnnouncement, setShowCreateAnnouncement] = useState(false);
    const [showCreateGoal, setShowCreateGoal] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [completionNote, setCompletionNote] = useState('');
    const [showManageMembers, setShowManageMembers] = useState(false);

    // Track views for announcements when they are first loaded
    const trackedViewsRef = useRef(new Set());

    const loadDepartmentData = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_department_detail.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    department_id: departmentId,
                    user_id: user.user_id
                })
            });

            const data = await response.json();
            if (data.success) {
                setDepartment(data.data.department);
                setGroup({
                    name: data.data.department.group_name,
                    group_photo: data.data.department.group_photo,
                    group_type: data.data.department.group_type
                });
                setAnnouncements(data.data.announcements || []);
                setGoals(data.data.goals || []);
                setUserRole(data.data.user_role);
                setIsDepartmentMember(data.data.is_department_member);
                setIsDepartmentLeader(data.data.is_department_leader);
            } else {
                setError(data.messages || 'Failed to load department data');
            }
        } catch (err) {
            setError('Failed to load department data');
            console.error('Error loading department data:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadTopPerformers = async () => {
        try {
            const response = await fetch(`${backendUrl}/get_department_top_performers.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    department_id: departmentId,
                    user_id: user.user_id,
                    limit: 5
                })
            });
            const data = await response.json();
            if (data.success) {
                setTopPerformers(data.data);
            }
        } catch (err) {
            console.error('Error loading top performers:', err);
        }
    };

    const trackAnnouncementView = async (announcementId) => {
        try {
            const response = await fetch(`${backendUrl}/track_announcement_view.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    announcement_id: announcementId,
                    user_id: user.user_id
                })
            });

            const data = await response.json();
            if (data.success && !data.data.already_viewed) {
                console.log(`View tracked for announcement ${announcementId}`);
            }
        } catch (err) {
            console.error('Error tracking announcement view:', err);
        }
    };

    const markGoalComplete = async () => {
        if (!completionNote.trim()) {
            showAlert('Please add a note when marking goal as complete', 'error');
            return;
        }

        try {
            const response = await fetch(`${backendUrl}/mark_department_goal_complete.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    goal_id: selectedGoal.id,
                    user_id: user.user_id,
                    note: completionNote.trim()
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setShowCompletionModal(false);
                setCompletionNote('');
                setSelectedGoal(null);
                loadDepartmentData(); // Refresh goals list
                showAlert('Goal marked as complete successfully!', 'success');
            } else {
                showAlert(data.messages || 'Failed to mark goal as complete', 'error');
            }
        } catch (err) {
            showAlert('Failed to mark goal as complete', 'error');
            console.error('Error marking goal complete:', err);
        }
    };

    useEffect(() => {
        if (user && user.user_id && departmentId) {
            loadDepartmentData();
            loadTopPerformers();
        }
    }, [user.user_id, departmentId]);

    useEffect(() => {
        if (announcements.length > 0 && user && user.user_id) {
            announcements.forEach(announcement => {
                if (!trackedViewsRef.current.has(announcement.id)) {
                    trackAnnouncementView(announcement.id);
                    trackedViewsRef.current.add(announcement.id);
                }
            });
        }
    }, [announcements, user.user_id]);

    // Helper function to safely parse dates for Safari compatibility
    const parseDate = (dateString) => {
        if (!dateString) return null;
        
        if (dateString instanceof Date) return dateString;
        
        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
        if (match) {
            const [, year, month, day, hour, minute, second] = match;
            const date = new Date(
                parseInt(year, 10),
                parseInt(month, 10) - 1,
                parseInt(day, 10),
                parseInt(hour, 10),
                parseInt(minute, 10),
                parseInt(second, 10)
            );
            
            return isNaN(date.getTime()) ? null : date;
        }
        
        try {
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? null : date;
        } catch (error) {
            console.error('Error parsing date:', dateString, error);
            return null;
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        
        const date = parseDate(timestamp);
        if (!date) return 'Invalid date';
        
        const now = new Date();
        const diff = now - date;
        
        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
        
        return date.toLocaleDateString();
    };

    const getGroupTypeIcon_bk = (type) => {
        switch (type) {
            case 'church': return '⛪';
            case 'ministry': return '🙏';
            case 'community': return '👥';
            default: return '⛪';
        }
    };

    const getGroupTypeIcon = (type) => {
        return '⛪';
    };

    const getGroupTypeColor = (type) => {
        switch (type) {
            case 'church': return 'text-primary';
            case 'ministry': return 'text-success';
            case 'community': return 'text-info';
            default: return 'text-secondary';
        }
    };

    const getFrequencyDisplay = (frequency) => {
        switch (frequency) {
            case 'one-off': return 'One-time';
            case 'daily': return 'Daily';
            case 'weekly': return 'Weekly';
            case 'monthly': return 'Monthly';
            default: return frequency;
        }
    };

    const isGoalExpired = (endDate) => {
        if (!endDate) return false;
        
        const isoDateString = endDate.replace(' ', 'T');
        const end = new Date(isoDateString);
        
        if (isNaN(end.getTime())) return false;
        
        const now = new Date();
        return end < now;
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="ms-3">Loading department...</div>
            </div>
        );
    }

    if (error || !department) {
        return (
            <div className="container-fluid p-3">
                <div className="alert alert-danger" role="alert">
                    {error || 'Department not found'}
                </div>
            </div>
        );
    }

    if (!isDepartmentMember) {
        return (
            <div className="container-fluid p-3">
                <div className="alert alert-warning" role="alert">
                    <h5>Access Restricted</h5>
                    <p>You are not a member of this department. Only department members can view this page.</p>
                    <button 
                        className="btn btn-primary"
                        onClick={() => navigate(`/group/${groupId}`)}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Back to Group
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100 d-flex flex-column">
            <Header />
            
            <div className="container-fluid p-3 flex-grow-1" style={{ marginTop: '80px', marginBottom: '60px' }}>

                {/* Header with Group and Department Info */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-start mb-3">
                                    <div className="flex-grow-1">
                                        <div className="d-flex align-items-center mb-2">
                                            {/* Group Info */}
                                            <div className="me-3">
                                                {group.group_photo ? (
                                                    <img 
                                                        src={group.group_photo} 
                                                        alt={group.name}
                                                        className="rounded-circle"
                                                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <div 
                                                        className="rounded-circle d-flex align-items-center justify-content-center"
                                                        style={{ 
                                                            width: '50px', 
                                                            height: '50px', 
                                                            backgroundColor: '#e9ecef',
                                                            fontSize: '20px'
                                                        }}
                                                    >
                                                        {getGroupTypeIcon(group.group_type)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="me-3">
                                                <h6 className="mb-0 text-muted">{group.name}</h6>
                                                <small className="text-muted">{group.group_type.charAt(0).toUpperCase() + group.group_type.slice(1)} Group</small>
                                            </div>
                                            
                                            {/* Department Info */}
                                            <div className="me-3">
                                                {department.logo_url ? (
                                                    <img 
                                                        src={department.logo_url} 
                                                        alt={department.name}
                                                        className="rounded-circle"
                                                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <div 
                                                        className="rounded-circle d-flex align-items-center justify-content-center"
                                                        style={{ 
                                                            width: '50px', 
                                                            height: '50px', 
                                                            backgroundColor: '#e9ecef',
                                                            fontSize: '20px'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faBuilding} />
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <h2 className="mb-0">{department.name}</h2>
                                                <small className="text-muted">Department</small>
                                            </div>
                                        </div>
                                        
                                        {department.description && (
                                            <div 
                                                className="text-muted mb-0"
                                                dangerouslySetInnerHTML={{ __html: department.description }}
                                            />
                                        )}
                                        
                                        <div className="d-flex align-items-center gap-3 mt-2">
                                            <span className="text-muted">
                                                <FontAwesomeIcon icon={faUsers} className="me-1" />
                                                {department.members?.length || 0} member{(department.members?.length || 0) !== 1 ? 's' : ''}
                                            </span>
                                            <span className="text-muted">
                                                <FontAwesomeIcon icon={faBuilding} className="me-1" />
                                                Leader: {department.leader_first_name} {department.leader_last_name}
                                            </span>
                                            {isDepartmentLeader && (
                                                <span className="badge bg-warning">
                                                    <FontAwesomeIcon icon={faCrown} className="me-1" />
                                                    Leader
                                                </span>
                                            )}
                                        </div>
                                        
                                        {/* Action Buttons */}
                                        <div className="d-flex gap-2 mt-3">
                                            {userRole === 'admin' && (
                                                <button 
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => setShowManageMembers(true)}
                                                >
                                                    <FontAwesomeIcon icon={faUsers} className="me-1" />
                                                    Manage Members
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Feature Cards */}
                <div className="row">
                    {/* Announcements Card */}
                    <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="me-3">
                                        <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                                             style={{ width: '50px', height: '50px' }}>
                                            <FontAwesomeIcon icon={faBell} />
                                        </div>
                                    </div>
                                    <div>
                                        <h5 className="card-title mb-0">Announcements</h5>
                                        <small className="text-muted">Department updates</small>
                                    </div>
                                </div>
                                
                                <div className="flex-grow-1 announcements-container">
                                    {announcements.length > 0 ? (
                                        <div className="mb-3">
                                            {announcements.slice(0, 1).map((announcement) => (
                                                <div key={announcement.id} className="mb-2 p-2 bg-light rounded">
                                                    <div className="d-flex align-items-start">
                                                        {announcement.is_important && (
                                                            <FontAwesomeIcon 
                                                                icon={faExclamationTriangle} 
                                                                className="text-warning me-2 mt-1" 
                                                            />
                                                        )}
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-1">{announcement.title}</h6>
                                                            <div className="d-md-none">
                                                                <p className="mb-1 small text-muted">
                                                                    {announcement.message.length > 100 
                                                                        ? announcement.message.substring(0, 100) + '...'
                                                                        : announcement.message
                                                                    }
                                                                </p>
                                                                {announcement.image_url && (
                                                                    <div className="mb-2 position-relative">
                                                                        <img 
                                                                            src={announcement.image_url} 
                                                                            alt="Announcement flyer" 
                                                                            className="img-fluid rounded"
                                                                            style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover' }}
                                                                        />
                                                                    </div>
                                                                )}
                                                                {announcement.has_url && (
                                                                    <a 
                                                                        href={announcement.url} 
                                                                        target="_blank" 
                                                                        rel="noopener noreferrer"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                    >
                                                                        <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                                                                        {announcement.url_text || 'View Link'}
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <small className="text-muted">
                                                            {formatTime(announcement.created_at)}
                                                        </small>
                                                        <small className="text-muted d-flex align-items-center">
                                                            <FontAwesomeIcon icon={faEye} className="me-1" />
                                                            {announcement.view_count || 0}
                                                        </small>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted">No announcements yet</p>
                                    )}
                                </div>
                                
                                <div className="d-flex gap-2">
                                    <button 
                                        className="btn btn-outline-primary btn-sm flex-grow-1"
                                        onClick={() => navigate(`/group/${groupId}/department/${departmentId}/announcements`)}
                                    >
                                        View All
                                    </button>
                                    {isDepartmentLeader && (
                                        <button 
                                            className="btn btn-primary btn-sm"
                                            onClick={() => setShowCreateAnnouncement(true)}
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Goals Card */}
                    <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="me-3">
                                        <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center" 
                                             style={{ width: '50px', height: '50px' }}>
                                            🎯
                                        </div>
                                    </div>
                                    <div>
                                        <h5 className="card-title mb-0">Department Goals</h5>
                                        <small className="text-muted">Track objectives</small>
                                    </div>
                                </div>
                                
                                <div className="flex-grow-1">
                                    {goals.length > 0 ? (
                                        <div className="mb-3">
                                            {goals.slice(0, 1).map((goal) => (
                                                <div key={goal.id} className="mb-2 p-2 bg-light rounded">
                                                    <h6 className="mb-1">{goal.title}</h6>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <small className="text-muted">
                                                            {goal.completion_count}/{goal.total_members} completed
                                                        </small>
                                                        <small className="text-muted">
                                                            {goal.completion_percentage}%
                                                        </small>
                                                    </div>
                                                    <div className="progress mt-1" style={{ height: '4px' }}>
                                                        <div 
                                                            className="progress-bar bg-success" 
                                                            style={{ width: `${goal.completion_percentage}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted">No goals yet</p>
                                    )}
                                </div>
                                
                                <div className="d-flex gap-2">
                                    <button 
                                        className="btn btn-outline-success btn-sm flex-grow-1"
                                        onClick={() => navigate(`/group/${groupId}/department/${departmentId}/goals`)}
                                    >
                                        View Goals
                                    </button>
                                    {isDepartmentLeader && (
                                        <button 
                                            className="btn btn-success btn-sm"
                                            onClick={() => setShowCreateGoal(true)}
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Performers Card */}
                    <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="me-3">
                                        <div className="bg-warning text-white rounded-circle d-flex align-items-center justify-content-center" 
                                             style={{ width: '50px', height: '50px' }}>
                                            <FontAwesomeIcon icon={faTrophy} />
                                        </div>
                                    </div>
                                    <div>
                                        <h5 className="card-title mb-0">Top Performers</h5>
                                        <small className="text-muted">Goal champions</small>
                                    </div>
                                </div>
                                
                                <div className="flex-grow-1">
                                    {topPerformers && topPerformers.all_time_performers.length > 0 ? (
                                        <div className="top-performers-preview">
                                            {topPerformers.all_time_performers.slice(0, 3).map((performer, index) => (
                                                <div key={performer.user_id} className="d-flex align-items-center mb-2">
                                                    <div className="me-2">
                                                        {index === 0 && <FontAwesomeIcon icon={faCrown} className="text-warning" />}
                                                        {index === 1 && <FontAwesomeIcon icon={faMedal} className="text-secondary" />}
                                                        {index === 2 && <FontAwesomeIcon icon={faMedal} className="text-warning" />}
                                                        {index > 2 && <span className="badge bg-secondary">#{index + 1}</span>}
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="small fw-bold">
                                                            {performer.first_name} {performer.last_name}
                                                        </div>
                                                        <div className="small text-muted">
                                                            {performer.total_completions} completions
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted small">No goal completions yet</p>
                                    )}
                                </div>
                                
                                <button 
                                    className="btn btn-outline-warning btn-sm"
                                    onClick={() => navigate(`/group/${groupId}/department/${departmentId}/goals`)}
                                >
                                    View All Goals
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />

            {/* Create Announcement Modal */}
            {showCreateAnnouncement && (
                <CreateAnnouncementModal 
                    departmentId={departmentId}
                    onClose={() => setShowCreateAnnouncement(false)}
                    onAnnouncementCreated={loadDepartmentData}
                />
            )}

            {/* Create Goal Modal */}
            {showCreateGoal && (
                <CreateGoalModal 
                    departmentId={departmentId}
                    onClose={() => setShowCreateGoal(false)}
                    onGoalCreated={loadDepartmentData}
                />
            )}

            {/* Manage Members Modal */}
            {showManageMembers && (
                <ManageMembersModal 
                    department={department}
                    onClose={() => setShowManageMembers(false)}
                    onMembersUpdated={loadDepartmentData}
                />
            )}

            {/* Goal Completion Modal */}
            {showCompletionModal && selectedGoal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Mark Goal as Complete</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowCompletionModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="goal-preview mb-3">
                                    <h6>{selectedGoal.title}</h6>
                                    <p className="text-muted">{selectedGoal.description}</p>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="completionNote" className="form-label">Completion Note *</label>
                                    <textarea
                                        className="form-control"
                                        id="completionNote"
                                        rows="4"
                                        value={completionNote}
                                        onChange={(e) => setCompletionNote(e.target.value)}
                                        placeholder="Add a note about how you completed this goal..."
                                        required
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowCompletionModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-success"
                                    onClick={markGoalComplete}
                                    disabled={!completionNote.trim()}
                                >
                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                    Mark Complete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

// Create Announcement Modal Component
const CreateAnnouncementModal = ({ departmentId, onClose, onAnnouncementCreated }) => {
    const { user } = useContext(MyContext);
    const { showAlert, AlertComponent } = useCustomAlert();
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        url: '',
        url_text: '',
        is_important: false
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            setError('Title is required');
            return;
        }
        
        if (!formData.message.trim()) {
            setError('Message is required');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            let imageUrl = null;
            
            if (imageFile) {
                const uploadFormData = new FormData();
                uploadFormData.append('image', imageFile);
                uploadFormData.append('user_id', user.user_id);
                uploadFormData.append('department_id', departmentId);
                
                const uploadResponse = await fetch(`${backendUrl}/upload_to_cloudinary.php`, {
                    method: 'POST',
                    body: uploadFormData
                });
                
                const uploadData = await uploadResponse.json();
                
                if (uploadData.success && uploadData.data && uploadData.data.secure_url) {
                    imageUrl = uploadData.data.secure_url;
                } else {
                    setError(uploadData.messages || 'Failed to upload image');
                    return;
                }
            }

            const announcementData = {
                department_id: departmentId,
                sender_id: user.user_id,
                title: formData.title.trim(),
                message: formData.message.trim(),
                url: formData.url.trim() || null,
                url_text: formData.url_text.trim() || null,
                is_important: formData.is_important ? 1 : 0,
                image_url: imageUrl
            };
            
            const response = await fetch(`${backendUrl}/create_department_announcement.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(announcementData)
            });

            const data = await response.json();

            if (data.success) {
                onAnnouncementCreated();
                onClose();
                showAlert('Announcement created successfully!', 'success');
            } else {
                setError(data.messages || 'Failed to create announcement');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error creating announcement:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Create Department Announcement</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="title" className="form-label">Title *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    placeholder="Enter announcement title"
                                    maxLength={255}
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="message" className="form-label">Message *</label>
                                <textarea
                                    className="form-control"
                                    id="message"
                                    rows="4"
                                    value={formData.message}
                                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                                    placeholder="Enter announcement message"
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="image" className="form-label">Flyer/Image (Optional)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="image"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setImageFile(file);
                                            const reader = new FileReader();
                                            reader.onload = (e) => setImagePreview(e.target.result);
                                            reader.readAsDataURL(file);
                                        } else {
                                            setImageFile(null);
                                            setImagePreview(null);
                                        }
                                    }}
                                />
                                {imagePreview && (
                                    <div className="mt-2">
                                        <img 
                                            src={imagePreview} 
                                            alt="Preview" 
                                            className="img-thumbnail" 
                                            style={{ maxWidth: '200px', maxHeight: '200px' }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="mb-3">
                                <label htmlFor="url" className="form-label">Link URL (Optional)</label>
                                <input
                                    type="url"
                                    className="form-control"
                                    id="url"
                                    value={formData.url}
                                    onChange={(e) => setFormData({...formData, url: e.target.value})}
                                    placeholder="https://example.com"
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="url_text" className="form-label">Link Text (Optional)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="url_text"
                                    value={formData.url_text}
                                    onChange={(e) => setFormData({...formData, url_text: e.target.value})}
                                    placeholder="Click here to view"
                                />
                            </div>

                            <div className="mb-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="is_important"
                                        checked={formData.is_important}
                                        onChange={(e) => setFormData({...formData, is_important: e.target.checked})}
                                    />
                                    <label className="form-check-label" htmlFor="is_important">
                                        Mark as important
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Creating...
                                    </>
                                ) : (
                                    'Create Announcement'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

// Create Goal Modal Component
const CreateGoalModal = ({ departmentId, onClose, onGoalCreated }) => {
    const { user } = useContext(MyContext);
    const { showAlert, AlertComponent } = useCustomAlert();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        frequency: 'one-off'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim() || !formData.description.trim() || !formData.start_date) {
            setError('Title, description, and start date are required');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const goalData = {
                department_id: departmentId,
                created_by: user.user_id,
                title: formData.title.trim(),
                description: formData.description.trim(),
                start_date: formData.start_date,
                end_date: formData.end_date || null,
                frequency: formData.frequency
            };
            
            const response = await fetch(`${backendUrl}/create_department_goal.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(goalData)
            });

            const data = await response.json();

            if (data.success) {
                onGoalCreated();
                onClose();
                showAlert('Goal created successfully!', 'success');
            } else {
                setError(data.messages || 'Failed to create goal');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error creating goal:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Create Department Goal</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="title" className="form-label">Title *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    placeholder="Enter goal title"
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="description" className="form-label">Description *</label>
                                <textarea
                                    className="form-control"
                                    id="description"
                                    rows="4"
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    placeholder="Enter goal description"
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="start_date" className="form-label">Start Date *</label>
                                <input
                                    type="datetime-local"
                                    className="form-control"
                                    id="start_date"
                                    value={formData.start_date}
                                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="end_date" className="form-label">End Date (Optional)</label>
                                <input
                                    type="datetime-local"
                                    className="form-control"
                                    id="end_date"
                                    value={formData.end_date}
                                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                                />
                                <small className="form-text text-muted">Leave empty for ongoing goals</small>
                            </div>

                            <div className="mb-3">
                                <label htmlFor="frequency" className="form-label">Frequency</label>
                                <select
                                    className="form-control"
                                    id="frequency"
                                    value={formData.frequency}
                                    onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                                >
                                    <option value="one-off">One-time</option>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                                <small className="form-text text-muted">
                                    {formData.frequency === 'one-off' ? 'Goal can be completed once' : 
                                     formData.frequency === 'daily' ? 'Goal resets every day' :
                                     formData.frequency === 'weekly' ? 'Goal resets every week' :
                                     'Goal resets every month'}
                                </small>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-success"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Creating...
                                    </>
                                ) : (
                                    'Create Goal'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

// Manage Members Modal Component
const ManageMembersModal = ({ department, onClose, onMembersUpdated }) => {
    const { user } = useContext(MyContext);
    const { showAlert, AlertComponent } = useCustomAlert();
    const [currentMembers, setCurrentMembers] = useState([]);
    const [availableMembers, setAvailableMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addingMember, setAddingMember] = useState(false);
    const [removingMember, setRemovingMember] = useState(false);

    useEffect(() => {
        if (department) {
            loadMembers();
        }
    }, [department]);

    const loadMembers = async () => {
        try {
            setLoading(true);
            
            // Load current department members
            setCurrentMembers(department.members || []);
            
            // Load available members (group members not in department)
            const response = await fetch(`${backendUrl}/get_available_department_members.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    department_id: department.id,
                    user_id: user.user_id
                })
            });

            const data = await response.json();
            if (data.success) {
                setAvailableMembers(data.data || []);
            } else {
                showAlert('error', data.messages || 'Failed to load available members');
            }
        } catch (err) {
            showAlert('error', 'Failed to load members');
            console.error('Error loading members:', err);
        } finally {
            setLoading(false);
        }
    };

    const addMemberToDepartment = async (memberToAdd) => {
        try {
            setAddingMember(true);
            
            const response = await fetch(`${backendUrl}/add_department_member.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    department_id: department.id,
                    user_id: memberToAdd.user_id,
                    added_by: user.user_id
                })
            });

            const data = await response.json();
            if (data.success) {
                showAlert('success', `${memberToAdd.first_name} ${memberToAdd.last_name} has been added to the department`);
                loadMembers(); // Refresh the member lists
                onMembersUpdated(); // Refresh department data
            } else {
                showAlert('error', data.messages || 'Failed to add member');
            }
        } catch (err) {
            showAlert('error', 'Failed to add member');
            console.error('Error adding member:', err);
        } finally {
            setAddingMember(false);
        }
    };

    const removeMemberFromDepartment = async (memberToRemove) => {
        try {
            setRemovingMember(true);
            
            const response = await fetch(`${backendUrl}/remove_department_member.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    department_id: department.id,
                    user_id: memberToRemove.user_id,
                    removed_by: user.user_id
                })
            });

            const data = await response.json();
            if (data.success) {
                showAlert('success', `${memberToRemove.first_name} ${memberToRemove.last_name} has been removed from the department`);
                loadMembers(); // Refresh the member lists
                onMembersUpdated(); // Refresh department data
            } else {
                showAlert('error', data.messages || 'Failed to remove member');
            }
        } catch (err) {
            showAlert('error', 'Failed to remove member');
            console.error('Error removing member:', err);
        } finally {
            setRemovingMember(false);
        }
    };

    if (loading) {
        return (
            <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Manage Department Members</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <div className="mt-2">Loading members...</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Manage Department Members</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                {/* Current Members */}
                                <div className="col-md-6">
                                    <h6 className="mb-3">
                                        <FontAwesomeIcon icon={faUsers} className="me-2" />
                                        Current Members ({currentMembers.length})
                                    </h6>
                                    <div className="member-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        {currentMembers.length === 0 ? (
                                            <div className="text-center text-muted py-3">
                                                <FontAwesomeIcon icon={faUsers} size="2x" className="mb-2" />
                                                <div>No members yet</div>
                                            </div>
                                        ) : (
                                            currentMembers.map((member) => (
                                                <div key={member.user_id} className="d-flex align-items-center justify-content-between p-2 border rounded mb-2">
                                                    <div className="d-flex align-items-center">
                                                        {member.photo ? (
                                                            <img 
                                                                src={member.photo} 
                                                                alt={member.first_name}
                                                                className="rounded-circle me-2"
                                                                style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-2"
                                                                 style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                                                                {member.first_name.charAt(0)}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className="fw-medium">{member.first_name} {member.last_name}</div>
                                                            <small className="text-muted">@{member.username}</small>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {member.role === 'leader' && (
                                                            <span className="badge bg-warning">
                                                                <FontAwesomeIcon icon={faCrown} className="me-1" />
                                                                Leader
                                                            </span>
                                                        )}
                                                        <button 
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => removeMemberFromDepartment(member)}
                                                            disabled={removingMember || member.role === 'leader'}
                                                            title={member.role === 'leader' ? 'Cannot remove department leader' : 'Remove member'}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* Available Members */}
                                <div className="col-md-6">
                                    <h6 className="mb-3">
                                        <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                                        Available Members ({availableMembers.length})
                                    </h6>
                                    <div className="member-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        {availableMembers.length === 0 ? (
                                            <div className="text-center text-muted py-3">
                                                <FontAwesomeIcon icon={faUserPlus} size="2x" className="mb-2" />
                                                <div>All group members are already in this department</div>
                                            </div>
                                        ) : (
                                            availableMembers.map((member) => (
                                                <div key={member.user_id} className="d-flex align-items-center justify-content-between p-2 border rounded mb-2">
                                                    <div className="d-flex align-items-center">
                                                        {member.photo ? (
                                                            <img 
                                                                src={member.photo} 
                                                                alt={member.first_name}
                                                                className="rounded-circle me-2"
                                                                style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-2"
                                                                 style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                                                                {member.first_name.charAt(0)}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className="fw-medium">{member.first_name} {member.last_name}</div>
                                                            <small className="text-muted">@{member.username}</small>
                                                        </div>
                                                    </div>
                                                    <button 
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() => addMemberToDepartment(member)}
                                                        disabled={addingMember}
                                                    >
                                                        <FontAwesomeIcon icon={faPlus} />
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <AlertComponent />
        </>
    );
};

export default DepartmentDetail;
